/**
 * Client for the support service (intercom.brainhi.com).
 *
 * A separate axios instance from the shared one because this talks to a
 * different host, but it installs the same auth interceptors: the access token
 * lives 30 minutes, so without the refresh half an agent sitting in the inbox
 * would start failing silently while the rest of Orbit kept working.
 *
 * It sends the same cosmos access token; the support service validates it by
 * asking cosmos who the bearer is and refuses anyone who is not BrainHi staff.
 */

import axios, { AxiosResponse } from 'axios';
import { attachAuthInterceptors } from 'src/common/utils';

import {
  Analytics,
  Conversation,
  ConversationDetail,
  ConversationState,
  DirectoryAccount,
  DirectoryMember,
  Label,
  Macro,
  Message,
} from './types';

export const SUPPORT_URL = process.env.REACT_APP_SUPPORT_URL || '';

const API = attachAuthInterceptors(axios.create({ baseURL: SUPPORT_URL }));

// `all` exists for search: who the agent is looking for matters more than
// which tab their conversations happen to sit in.
export const fetchConversations = (
  state: ConversationState | 'all',
  q = ''
): Promise<AxiosResponse<Conversation[]>> => {
  const params = new URLSearchParams({ state });
  if (q) params.set('q', q);
  return API.get(`/api/agent/conversations?${params.toString()}`);
};

export const fetchContactConversations = (
  contactId: string
): Promise<AxiosResponse<Conversation[]>> =>
  API.get(`/api/agent/contacts/${contactId}/conversations`);

export const fetchConversation = (id: string): Promise<AxiosResponse<ConversationDetail>> =>
  API.get(`/api/agent/conversations/${id}`);

// The people support can write to first live in cosmos, not in the support
// service's contacts — most have never opened the chat. The service asks cosmos
// on the agent's behalf, so the inbox still only talks to one host.
export const searchDirectoryAccounts = (q: string): Promise<AxiosResponse<DirectoryAccount[]>> =>
  API.get(`/api/agent/directory/accounts?${new URLSearchParams({ q }).toString()}`);

export const fetchDirectoryMembers = (
  accountId: string
): Promise<AxiosResponse<DirectoryMember[]>> =>
  API.get(`/api/agent/directory/accounts/${accountId}/members`);

// 201 for a new thread, 200 when the person already had one open and the
// message went there instead.
export const startConversation = (payload: {
  account_id: string;
  user_id: string;
  body: string;
}): Promise<AxiosResponse<ConversationDetail>> => API.post('/api/agent/conversations', payload);

export const sendReply = (id: string, body: string): Promise<AxiosResponse<Message>> =>
  API.post(`/api/agent/conversations/${id}/messages`, { body });

// An internal note: lands in the thread for the team, the customer never
// sees it and nothing about the conversation moves.
export const sendNote = (id: string, body: string): Promise<AxiosResponse<Message>> =>
  API.post(`/api/agent/conversations/${id}/notes`, { body });

export const uploadAttachment = (id: string, file: File): Promise<AxiosResponse<Message>> => {
  const form = new FormData();
  form.append('file', file, file.name);
  return API.post(`/api/agent/conversations/${id}/attachments`, form);
};

// The endpoint wants the agent's token, which <img src> cannot send — so the
// bytes come over axios and the tag gets a blob URL.
export const fetchAttachmentBlob = (id: string): Promise<AxiosResponse<Blob>> =>
  API.get(`/api/agent/attachments/${id}`, { responseType: 'blob' });

export const updateConversation = (
  id: string,
  payload: { state: ConversationState }
): Promise<AxiosResponse<Conversation>> => API.patch(`/api/agent/conversations/${id}`, payload);

export const fetchLabels = (): Promise<AxiosResponse<Label[]>> => API.get('/api/agent/labels');

export const createLabel = (payload: { name: string }): Promise<AxiosResponse<Label>> =>
  API.post('/api/agent/labels', payload);

export const updateLabel = (
  id: string,
  payload: { name?: string; archived?: boolean }
): Promise<AxiosResponse<Label>> => API.patch(`/api/agent/labels/${id}`, payload);

// A replace of the whole set: the picker is a checkbox list, and what it
// submits is the state of the checkboxes, not a diff.
export const setConversationLabels = (
  id: string,
  labelIds: string[]
): Promise<AxiosResponse<Conversation>> =>
  API.put(`/api/agent/conversations/${id}/labels`, { label_ids: labelIds });

export const fetchMacros = (): Promise<AxiosResponse<Macro[]>> => API.get('/api/agent/macros');

export const createMacro = (payload: {
  title: string;
  body: string;
}): Promise<AxiosResponse<Macro>> => API.post('/api/agent/macros', payload);

export const updateMacro = (
  id: string,
  payload: { title: string; body: string }
): Promise<AxiosResponse<Macro>> => API.put(`/api/agent/macros/${id}`, payload);

export const deleteMacro = (id: string): Promise<AxiosResponse<void>> =>
  API.delete(`/api/agent/macros/${id}`);

// Resolution and satisfaction numbers over the last `days`, bucketed by day in
// the agent's own timezone.
export const fetchAnalytics = (days: number, tz: string): Promise<AxiosResponse<Analytics>> => {
  const params = new URLSearchParams({ days: String(days), tz });
  return API.get(`/api/agent/analytics?${params.toString()}`);
};

/**
 * EventSource cannot send an Authorization header, so the stream credential goes
 * in the URL. This mints a short-lived token that opens a stream and nothing
 * else — the cosmos token itself must never end up in a URL.
 */
export const fetchStreamToken = (): Promise<AxiosResponse<{ token: string }>> =>
  API.post('/api/agent/stream-token');
